import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/common/Icon/Icon';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import FiltersGroup from '../../components/common/FiltersGroup/FiltersGroup';
import ToolbarRow from '../../components/common/ToolbarRow/ToolbarRow';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import Loading from '../../components/common/Loading/Loading';
import DataTable from '../../components/common/DataTable/DataTable';
import Pagination from '../../components/common/Pagination/Pagination';
import PageSizeSelect from '../../components/common/PageSizeSelect/PageSizeSelect';
import Badge from '../../components/common/Badge/Badge';
import Button from '../../components/common/Button/Button';
import ProjectBadge from '../../components/common/ProjectBadge/ProjectBadge';
import ReactivatedBadge from '../../components/common/ReactivatedBadge/ReactivatedBadge';
import SensitiveStudentBadge from '../../components/common/SensitiveStudentBadge/SensitiveStudentBadge';
import PregnantStudentBadge from '../../components/common/PregnantStudentBadge/PregnantStudentBadge';
import RedFlagBadge from '../../components/common/RedFlagBadge/RedFlagBadge';
import ViewLinkButton from '../../components/common/ViewLinkButton/ViewLinkButton';
import CopyStudentNameButton from '../../components/common/CopyStudentNameButton/CopyStudentNameButton';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import KebabMenu from '../../components/common/KebabMenu/KebabMenu';
import SearchableSelect, { SelectOption } from '../../components/common/SearchableSelect/SearchableSelect';
import FormInput from '../../components/common/FormInput/FormInput';
import FormTextarea from '../../components/common/FormTextarea/FormTextarea';
import StudentNotesHistorySection from '../../components/common/StudentNotesHistorySection/StudentNotesHistorySection';
import StudentNotesModal from '../../components/common/StudentNotesModal/StudentNotesModal';
import PageContainer from '../../components/common/PageContainer/PageContainer';
import { usePerformanceData } from '../../hooks/usePerformanceData';
import { usePageSize } from '../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { useAuth } from '../../contexts/AuthContext';
import { CoachService, StudentService, WorkoutAssignmentService } from '../../services';
import { WorkoutAssignmentRequestError } from '../../services/WorkoutAssignmentService';
import type { CoachOption } from '../../services/CoachService';
import type { StudentNoteItem } from '../../types/orchestrator';
import { NO_COACH_FILTER_VALUE } from '../../types/orchestrator';
import type { PerformanceFilters, PerformanceRow } from '../../types/performance';
import { performanceSupportReasonLabel } from '../../types/performanceSupport';
import { maskPhoneByCountry } from '../../utils/masks';
import { PERFORMANCE_STATUS_LABELS, PERFORMANCE_STATUS_OPTIONS } from '../../types/performance';
import {
  DEFAULT_STUDENT_TENURE_FILTER,
  type StudentTenureFilter,
} from '../../types/studentTenure';
import { STUDENT_JOURNEY_FILTER_OPTIONS } from '../../constants/commercialFilters';
import { resolveJourneyClassificationFilters } from '../../utils/studentClassificationFilters';
import {
  canAccessOrchestrator,
  canChangeStudentCoach,
  canDeliverWorkoutInPerformance,
  canInsertStudentWorkoutHistory,
} from '../../utils/permissions';
import { formatDate, formatDateTime, getDeliveredTrainingLabel, validateStudentNote } from '../../utils';
import { getBrazilCivilDate } from '../../utils/competence';
import { canWriteStudentObservations } from '../../utils/studentNotesModalAccess';
import ManagedFupResponseModal, {
  type ManagedFupDetails,
} from '../Orchestrator/ManagedFupResponseModal';
import NewStudentAnswersModal from '../../components/students/NewStudentAnswersModal/NewStudentAnswersModal';
import { StudentClassificationStatus, getStudentClassificationBadgeVariant } from '../../types';
import { useMarketingProjects } from '../../hooks/useMarketingProjects';
import type { TrainingItem } from '../../types/training';
import TrainingCatalogSearchModal from './components/TrainingCatalogSearchModal';
import LaunchWorkoutModal from './components/LaunchWorkoutModal';
import styles from './Performance.module.css';

const performanceSupportPhone = (row: PerformanceRow): string | null => {
  const digits = row.student_phone_number?.replace(/\D/g, '') ?? '';
  if (!digits) {
    return null;
  }

  const countryCode = row.student_phone_country_code?.replace(/\D/g, '') || '55';
  const masked = maskPhoneByCountry(digits, countryCode);
  return countryCode === '55' ? masked : `+${countryCode} ${masked}`;
};

const toDateOnly = (value: string | null | undefined): string => {
  const date = value?.slice(0, 10) ?? '';
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';
};

type PendingDeliveryState = {
  assignmentId: number;
  studentId: number;
  studentName: string;
  previousTrainingCode: string;
  nextTrainingCode: string;
  sourceTrainingId: number | null;
  selectedTrainingName: string | null;
  catalogTrainingLabel: string | null;
  deliveredDate: string;
  note: string;
  history: StudentNoteItem[];
};

type PendingNotesViewState = {
  studentId: number;
  studentName: string;
  history: StudentNoteItem[];
  newNote: string;
  summary: string | null;
};

type CoachEditModalState = {
  studentId: number;
  studentName: string;
  coachId: string;
};

type PendingFupViewState = ManagedFupDetails;

type NewStudentAnswersModalState = {
  studentId: number;
  studentName: string;
};

const PERFORMANCE_QUERY_SCHEMA = {
  page: queryFilter.integer(1, { min: 1 }),
  search: queryFilter.string(),
  coachIds: queryFilter.array<string>(),
  statuses: queryFilter.array<string>([], {
    allowed: PERFORMANCE_STATUS_OPTIONS.map((option) => option.value),
  }),
  studentTenure: queryFilter.enum<StudentTenureFilter>(
    DEFAULT_STUDENT_TENURE_FILTER,
    ['all', 'new', 'established'],
  ),
  classifications: queryFilter.array<string>(),
  projectCodes: queryFilter.array<string>(),
  expectedDeliveryDateStart: queryFilter.string(),
  expectedDeliveryDateEnd: queryFilter.string(),
  deliveredDateStart: queryFilter.string(),
  deliveredDateEnd: queryFilter.string(),
  sortOrder: queryFilter.enum<'asc' | 'desc'>('desc', ['asc', 'desc']),
};

/** Largura padrão do ConfirmModal (400px) + 30%. */
const DELIVERY_WORKOUT_MODAL_WIDTH_PX = 520;

const hasFupResponse = (row: PerformanceRow): boolean => row.has_fup_response;

const isNewStudentRow = (row: PerformanceRow): boolean => (
  row.classification?.status === StudentClassificationStatus.NEW_STUDENT
);

const canViewFup = (row: PerformanceRow): boolean => (
  isNewStudentRow(row) || hasFupResponse(row)
);

const PerformanceFupCell: React.FC<{
  row: PerformanceRow;
  onViewResponse: (row: PerformanceRow) => void;
}> = ({ row, onViewResponse }) => {
  if (!canViewFup(row)) {
    return (
      <div className={styles['summary-cell']}>
        <span className={styles['empty-value']}>—</span>
      </div>
    );
  }

  return (
    <div className={styles['summary-cell']}>
      <ViewLinkButton
        label="Ver resposta"
        icon={null}
        onClick={() => onViewResponse(row)}
      />
    </div>
  );
};

const Performance: React.FC = () => {
  const navigate = useNavigate();
  const { roles, loading: authLoading } = useAuth();
  const canChangeCoach = canChangeStudentCoach(roles);
  const canDeliverWorkout = canDeliverWorkoutInPerformance(roles);
  const canEditWorkoutHistory = canInsertStudentWorkoutHistory(roles);
  const canWriteObservations = canWriteStudentObservations(roles);
  const {
    filters: queryFilters,
    setFilter,
    setFilters,
  } = useQueryFilters(PERFORMANCE_QUERY_SCHEMA, { pageKey: 'page' });
  const {
    search,
    coachIds,
    statuses: statusFilters,
    studentTenure: studentTenureFilter,
    classifications: classificationFilters,
    projectCodes: projectCodeFilters,
    sortOrder,
    expectedDeliveryDateStart,
    expectedDeliveryDateEnd,
    deliveredDateStart,
    deliveredDateEnd,
  } = queryFilters;

  const [coachOptions, setCoachOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [coachAssignmentOptions, setCoachAssignmentOptions] = useState<CoachOption[]>([]);
  const [loadingCoachOptions, setLoadingCoachOptions] = useState(false);
  const [currentPortalUserId, setCurrentPortalUserId] = useState<number | null>(null);
  const appliedCoachDefaultRef = useRef(false);
  const [pendingCoachEdit, setPendingCoachEdit] = useState<CoachEditModalState | null>(null);
  const [submittingCoachEdit, setSubmittingCoachEdit] = useState(false);
  const canManageFup = canAccessOrchestrator(roles);
  const [pendingDelivery, setPendingDelivery] = useState<PendingDeliveryState | null>(null);
  const [isCatalogSearchOpen, setIsCatalogSearchOpen] = useState(false);
  const [catalogSearchTarget, setCatalogSearchTarget] = useState<'previous' | 'next'>('next');
  const [pendingNotesView, setPendingNotesView] = useState<PendingNotesViewState | null>(null);
  const [pendingFupView, setPendingFupView] = useState<PendingFupViewState | null>(null);
  const [launchWorkoutOpen, setLaunchWorkoutOpen] = useState(false);
  const [editingWorkoutRow, setEditingWorkoutRow] = useState<PerformanceRow | null>(null);
  const [pendingNewStudentAnswers, setPendingNewStudentAnswers] = useState<NewStudentAnswersModalState | null>(null);
  const [finalizingDelivery, setFinalizingDelivery] = useState(false);
  const [deliveryDateError, setDeliveryDateError] = useState<string | undefined>();
  const [submittingNotes, setSubmittingNotes] = useState(false);
  const [loadingNotesHistory, setLoadingNotesHistory] = useState(false);
  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('performance_page_size');

  const handleClearFilters = useCallback(() => {
    setFilters({
      search: '',
      coachIds: [],
      statuses: [],
      studentTenure: DEFAULT_STUDENT_TENURE_FILTER,
      classifications: [],
      projectCodes: [],
      expectedDeliveryDateStart: '',
      expectedDeliveryDateEnd: '',
      deliveredDateStart: '',
      deliveredDateEnd: '',
    });
  }, [setFilters]);

  const statusOptions = useMemo(
    () => PERFORMANCE_STATUS_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
    })),
    [],
  );
  const projects = useMarketingProjects();
  const projectFilterOptions = useMemo(
    () => projects.map((project) => ({
      value: project.code,
      label: project.code,
      color: project.colors.background,
    })),
    [projects],
  );
  const journeyFilters = useMemo(
    () => resolveJourneyClassificationFilters(classificationFilters, studentTenureFilter),
    [classificationFilters, studentTenureFilter],
  );

  useEffect(() => {
    let isCancelled = false;

    const loadCoaches = async () => {
      try {
        setLoadingCoachOptions(true);
        const viewer = await CoachService.listViewer();

        if (!isCancelled) {
          setCoachAssignmentOptions(viewer.options);
          setCurrentPortalUserId(viewer.currentPortalUserId);
          setCoachOptions([
            { value: NO_COACH_FILTER_VALUE, label: 'Sem Professora' },
            ...viewer.options.map((option) => ({
              value: String(option.id),
              label: option.label,
            })),
          ]);
        }
      } catch {
        if (!isCancelled) {
          setCoachAssignmentOptions([]);
          setCoachOptions([{ value: NO_COACH_FILTER_VALUE, label: 'Sem Professora' }]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingCoachOptions(false);
        }
      }
    };

    void loadCoaches();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (appliedCoachDefaultRef.current || authLoading) {
      return;
    }

    if (!roles.includes('coach') || roles.includes('admin')) {
      appliedCoachDefaultRef.current = true;
      return;
    }

    if (currentPortalUserId == null) {
      return;
    }

    appliedCoachDefaultRef.current = true;
    if (new URLSearchParams(window.location.search).get('coachIds')) {
      return;
    }

    setFilter('coachIds', [String(currentPortalUserId)]);
  }, [authLoading, currentPortalUserId, roles, setFilter]);

  const coachSelectOptions = useMemo<SelectOption[]>(
    () => coachAssignmentOptions.map((coach) => ({ value: String(coach.id), label: coach.label })),
    [coachAssignmentOptions],
  );

  const filters: PerformanceFilters = useMemo(
    () => ({
      search: search || undefined,
      coachIds: coachIds.length > 0 ? coachIds : undefined,
      statuses: statusFilters.length > 0 ? statusFilters : undefined,
      classifications: journeyFilters.length > 0 ? journeyFilters : undefined,
      projectCodes: projectCodeFilters.length > 0 ? projectCodeFilters : undefined,
      expectedDeliveryDateStart: expectedDeliveryDateStart || undefined,
      expectedDeliveryDateEnd: expectedDeliveryDateEnd || undefined,
      deliveredDateStart: deliveredDateStart || undefined,
      deliveredDateEnd: deliveredDateEnd || undefined,
      sortOrder,
    }),
    [search, coachIds, statusFilters, journeyFilters, projectCodeFilters, expectedDeliveryDateStart, expectedDeliveryDateEnd, deliveredDateStart, deliveredDateEnd, sortOrder],
  );

  const { rows, pagination, loading, error, refetch } = usePerformanceData(
    page,
    pageSize,
    filters,
  );
  const hasActiveFilters = !!(
    search
    || coachIds.length > 0
    || statusFilters.length > 0
    || journeyFilters.length > 0
    || projectCodeFilters.length > 0
    || expectedDeliveryDateStart
    || expectedDeliveryDateEnd
    || deliveredDateStart
    || deliveredDateEnd
  );

  const handleFinalizeDelivery = useCallback(async () => {
    if (!pendingDelivery) {
      return;
    }

    const sourceTrainingId = pendingDelivery.sourceTrainingId;
    const nextTrainingCode = sourceTrainingId != null
      ? (pendingDelivery.catalogTrainingLabel ?? '').trim()
      : pendingDelivery.nextTrainingCode.trim().toUpperCase();
    if (!nextTrainingCode) {
      alert(`Informe o ${getDeliveredTrainingLabel().toLowerCase()} antes de finalizar.`);
      return;
    }

    const deliveredDate = pendingDelivery.deliveredDate.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(deliveredDate)) {
      alert('Informe a data do treino.');
      return;
    }

    const deliveryNoteError = validateStudentNote(pendingDelivery.note, { required: false });
    if (deliveryNoteError) {
      alert(deliveryNoteError);
      return;
    }

    setFinalizingDelivery(true);
    setDeliveryDateError(undefined);
    try {
      const previousTrainingCode = pendingDelivery.previousTrainingCode.trim().toUpperCase();

      await WorkoutAssignmentService.deliver({
        assignmentId: pendingDelivery.assignmentId,
        nextTrainingCode,
        deliveredDate,
        note: pendingDelivery.note.trim() || undefined,
        ...(previousTrainingCode ? { previousTrainingCode } : {}),
        ...(sourceTrainingId != null ? { sourceTrainingId } : {}),
      });
      setPendingDelivery(null);
      setIsCatalogSearchOpen(false);
      refetch();
    } catch (deliveryError) {
      if (
        deliveryError instanceof WorkoutAssignmentRequestError
        && deliveryError.fields.delivered_date
      ) {
        setDeliveryDateError(deliveryError.fields.delivered_date);
        return;
      }
      alert(deliveryError instanceof Error ? deliveryError.message : 'Não foi possível finalizar a entrega.');
    } finally {
      setFinalizingDelivery(false);
    }
  }, [pendingDelivery, refetch]);

  const openFupViewModal = useCallback((row: PerformanceRow) => {
    if (isNewStudentRow(row) && !hasFupResponse(row)) {
      setPendingNewStudentAnswers({
        studentId: row.student_id,
        studentName: row.student_name,
      });
      return;
    }

    if (!hasFupResponse(row)) {
      return;
    }

    setPendingFupView({
      studentId: row.student_id,
      studentFupId: row.student_fup_id ?? 0,
      assignmentId: row.id,
      studentName: row.student_name,
      createdAt: null,
      notesSummary: row.notes_summary,
    });
  }, []);

  const openStudentNotesModal = useCallback(async (params: {
    studentId: number;
    studentName: string;
  }) => {
    setPendingNotesView({
      studentId: params.studentId,
      studentName: params.studentName,
      history: [],
      newNote: '',
      summary: null,
    });
    setLoadingNotesHistory(true);

    try {
      const { notes, summary } = await WorkoutAssignmentService.listStudentNotes(params.studentId);
      setPendingNotesView({
        studentId: params.studentId,
        studentName: params.studentName,
        history: notes,
        newNote: '',
        summary,
      });
    } catch (error) {
      setPendingNotesView(null);
      alert(error instanceof Error ? error.message : 'Não foi possível carregar as observações.');
    } finally {
      setLoadingNotesHistory(false);
    }
  }, []);

  const openDeliveryModal = useCallback(async (row: PerformanceRow) => {
    setIsCatalogSearchOpen(false);
    setPendingDelivery({
      assignmentId: row.id,
      studentId: row.student_id,
      studentName: row.student_name,
      previousTrainingCode: (
        row.current_training_code
        ?? (row.previous_workout_code !== 'Sem treino anterior' ? row.previous_workout_code : '')
      ).toUpperCase(),
      nextTrainingCode: (row.next_training_code ?? '').toUpperCase(),
      sourceTrainingId: null,
      selectedTrainingName: null,
      catalogTrainingLabel: null,
      deliveredDate: getBrazilCivilDate(),
      note: '',
      history: [],
    });
    setLoadingNotesHistory(true);

    try {
      const { notes } = await WorkoutAssignmentService.listStudentNotes(row.student_id);
      setPendingDelivery((current) => (
        current && current.studentId === row.student_id
          ? { ...current, history: notes }
          : current
      ));
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível carregar as observações.');
    } finally {
      setLoadingNotesHistory(false);
    }
  }, []);

  const openEditWorkoutModal = useCallback((row: PerformanceRow) => {
    setEditingWorkoutRow(row);
    setLaunchWorkoutOpen(true);
  }, []);

  const closeLaunchWorkoutModal = useCallback(() => {
    setLaunchWorkoutOpen(false);
    setEditingWorkoutRow(null);
  }, []);

  const handleNotesSubmit = useCallback(async () => {
    if (!pendingNotesView) {
      return;
    }

    const trimmedNote = pendingNotesView.newNote.trim();
    const noteError = validateStudentNote(trimmedNote);
    if (noteError) {
      alert(noteError);
      return;
    }

    setSubmittingNotes(true);
    try {
      await WorkoutAssignmentService.saveNotes({
        studentId: pendingNotesView.studentId,
        note: trimmedNote,
      });
      setPendingNotesView(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Não foi possível salvar as observações.');
    } finally {
      setSubmittingNotes(false);
    }
  }, [pendingNotesView]);

  const handleDeleteNote = useCallback(async (noteId: number) => {
    await WorkoutAssignmentService.deleteNote(noteId);

    const notesStudentId = pendingNotesView?.studentId ?? pendingDelivery?.studentId;
    if (!notesStudentId) {
      return;
    }

    const { notes } = await WorkoutAssignmentService.listStudentNotes(notesStudentId);
    setPendingNotesView((current) => (
      current && current.studentId === notesStudentId
        ? { ...current, history: notes }
        : current
    ));
    setPendingDelivery((current) => (
      current && current.studentId === notesStudentId
        ? { ...current, history: notes }
        : current
    ));
  }, [pendingDelivery?.studentId, pendingNotesView?.studentId]);

  const handleCoachEditConfirm = useCallback(async () => {
    if (!pendingCoachEdit) {
      return;
    }

    const selectedCoachId = Number.parseInt(pendingCoachEdit.coachId, 10);
    if (!Number.isFinite(selectedCoachId) || selectedCoachId <= 0) {
      alert('Selecione uma professora.');
      return;
    }

    setSubmittingCoachEdit(true);
    try {
      await StudentService.upsertCoachAssociation(
        pendingCoachEdit.studentId,
        selectedCoachId,
      );
      setPendingCoachEdit(null);
      refetch();
    } catch (coachEditError) {
      alert(coachEditError instanceof Error ? coachEditError.message : 'Não foi possível atualizar a professora.');
    } finally {
      setSubmittingCoachEdit(false);
    }
  }, [pendingCoachEdit, refetch]);

  if (error) {
    return <EmptyState message={`Erro: ${error}`} />;
  }

  return (
    <PageContainer className={styles.page}>
      <ToolbarRow>
        <Button
          variant="primary"
          size="small"
          className={styles['btn-launch-workout']}
          onClick={() => {
            setEditingWorkoutRow(null);
            setLaunchWorkoutOpen(true);
          }}
        >
          Lançar treino
        </Button>

        <RecordCount
          label="Performance:"
          count={pagination.totalRecords}
        />

        <FiltersGroup>
        <FilterGroup>
          <SearchFilterBar
            searchValue={search}
            onSearchChange={(value) => setFilter('search', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={coachIds}
            onChange={(values) => setFilter('coachIds', values)}
            options={coachOptions}
            placeholder="Professora"
            searchPlaceholder="Pesquisar"
            disabled={loadingCoachOptions}
            noResultsText="Nenhuma professora encontrada"
            clearButtonLabel="Limpar filtro"
            hideChips
            showSelectionCount={false}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            placeholder="Tag"
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhuma tag encontrada"
            clearButtonLabel="Limpar tags"
            hideChips
            showSelectionCount={false}
            dropdownClassName={styles['multi-select-dropdown']}
            groups={[
              ...(projectFilterOptions.length > 0
                ? [{
                    title: 'Projeto',
                    values: projectCodeFilters,
                    onChange: (values: string[]) => setFilter('projectCodes', values),
                    options: projectFilterOptions,
                  }]
                : []),
              {
                title: 'Jornada',
                values: journeyFilters,
                onChange: (values: string[]) => setFilters({
                  classifications: values,
                  studentTenure: DEFAULT_STUDENT_TENURE_FILTER,
                }),
                options: STUDENT_JOURNEY_FILTER_OPTIONS,
              },
            ]}
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={expectedDeliveryDateStart}
            dateEnd={expectedDeliveryDateEnd}
            onChange={(start, end) => setFilters({ expectedDeliveryDateStart: start, expectedDeliveryDateEnd: end })}
            placeholder="Data prevista"
            keepPlaceholder
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={deliveredDateStart}
            dateEnd={deliveredDateEnd}
            onChange={(start, end) => setFilters({ deliveredDateStart: start, deliveredDateEnd: end })}
            placeholder="Data de entrega"
            keepPlaceholder
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={statusFilters}
            onChange={(values) => setFilter('statuses', values)}
            options={statusOptions}
            placeholder="Status"
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhum status encontrado"
            clearButtonLabel="Limpar filtro"
            hideChips
            showSelectionCount={false}
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={handleClearFilters} />
        )}
        </FiltersGroup>
      </ToolbarRow>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState message="Nenhum ciclo encontrado com os filtros selecionados." />
      ) : (
        <DataTable
          headers={[
            'Nome',
            'Treino anterior',
            'Próximo treino',
            'FUP',
            'Observações',
            <span
              key="data-prevista"
              className={styles['sortable-header']}
              onClick={() => setFilter(
                'sortOrder',
                (currentSortOrder) => (currentSortOrder === 'asc' ? 'desc' : 'asc'),
              )}
            >
              Data prevista
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
              </span>
            </span>,
            'Data de entrega',
            <span key="status" className={styles['status-header']}>Status</span>,
            'Ações',
          ]}
          gridTemplate="minmax(210px, 1.45fr) 180px 110px 1.2fr 1.2fr 120px 120px 140px 190px"
          highlightTerm={search}
        >
          {rows.map((row) => {
            const canOpenDelivery = canDeliverWorkout && row.status === 'scheduled';
            const showEditWorkoutAction = canEditWorkoutHistory;
            const supportLabel = performanceSupportReasonLabel(row.performance_support_reason);
            const supportPhone = supportLabel ? performanceSupportPhone(row) : null;

            return (
              <DataTable.Row key={row.id} sensitive={Boolean(row.is_sensitive)}>
                <div className={styles['name-cell']}>
                  <div className={styles['student-name-row']}>
                    <button
                      type="button"
                      className={styles['student-name']}
                      onClick={() => navigate(`/alunas/${row.student_id}/editar`)}
                    >
                      {row.student_name}
                    </button>
                    <CopyStudentNameButton studentName={row.student_name} />
                  </div>
                  {row.student_email ? (
                    <span className={styles['student-email']}>{row.student_email}</span>
                  ) : (
                    <span className={styles['empty-value']}>E-mail não informado</span>
                  )}
                  {supportPhone ? (
                    <span className={styles['student-phone']}>{supportPhone}</span>
                  ) : null}
                  <div className={styles['coach-row']}>
                    <span className={styles['coach-label']}>Professora:</span>
                    {row.coach_name ? (
                      <span className={styles['coach-name']}>{row.coach_name}</span>
                    ) : (
                      <span className={styles['coach-empty']}>Sem professora</span>
                    )}
                    {canChangeCoach && (
                      <button
                        type="button"
                        className={styles['btn-edit-coach']}
                        title="Alterar professora"
                        aria-label="Alterar professora"
                        onClick={() => setPendingCoachEdit({
                          studentId: row.student_id,
                          studentName: row.student_name,
                          coachId: row.coach_id ? String(row.coach_id) : '',
                        })}
                      >
                        <Icon name="change_circle" size={16} />
                      </button>
                    )}
                  </div>
                  {(row.classification?.status === StudentClassificationStatus.NEW_STUDENT || row.project || row.active_pause || row.is_reactivated || supportLabel || row.is_sensitive || row.is_pregnant || row.is_red_flag) && (
                    <div className={styles['student-badges']}>
                      {row.active_pause && (
                        <Badge variant="info" solid>
                          Pausada até {formatDate(row.active_pause.paused_until)}
                        </Badge>
                      )}
                      {row.classification?.status === StudentClassificationStatus.NEW_STUDENT && (
                        <Badge
                          variant={getStudentClassificationBadgeVariant(row.classification)}
                          className={styles['new-student-badge']}
                        >
                          {row.classification.label}
                        </Badge>
                      )}
                      <ProjectBadge project={row.project} />
                      <ReactivatedBadge visible={row.is_reactivated} />
                      <SensitiveStudentBadge visible={row.is_sensitive} reason={row.sensitive_reason} />
                      <PregnantStudentBadge visible={row.is_pregnant} pregnantUntil={row.pregnant_until} />
                      <RedFlagBadge visible={row.is_red_flag} />
                      {supportLabel && (
                        <Badge variant="warning">
                          {supportLabel}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                <div className={styles['training-cell']}>
                  {row.current_training_code ? (
                    <span className={styles['code-text']}>{row.current_training_code}</span>
                  ) : (
                    <span className={styles['empty-value']}>Sem treino anterior</span>
                  )}
                </div>

                <div className={styles['code-cell']}>
                  {row.next_training_code ? (
                    <span className={styles['code-text']}>{row.next_training_code}</span>
                  ) : (
                    <span className={styles['empty-value']}>Não definido</span>
                  )}
                </div>

                <PerformanceFupCell row={row} onViewResponse={openFupViewModal} />

                <div className={styles['notes-cell']}>
                  <ViewLinkButton
                    label="Registrar"
                    icon="add_notes"
                    onClick={() => {
                      void openStudentNotesModal({
                        studentId: row.student_id,
                        studentName: row.student_name,
                      });
                    }}
                  />
                </div>

                <div className={styles['date-cell']}>
                  {row.expected_delivery_date ? (
                    <span>{formatDate(row.expected_delivery_date)}</span>
                  ) : (
                    <span className={styles['empty-value']}>Sem data</span>
                  )}
                </div>

                <div className={styles['date-cell']}>
                  {row.delivered_at ? (
                    <span>{formatDateTime(row.delivered_at)}</span>
                  ) : (
                    <span className={styles['empty-value']}>—</span>
                  )}
                </div>

                <div className={styles['status-cell']}>
                  <Badge
                    variant={row.status === 'delivered' ? 'success' : row.status === 'paused' ? 'info' : 'warning'}
                    solid
                    className={styles['delivery-badge']}
                  >
                    {PERFORMANCE_STATUS_LABELS[row.status]}
                  </Badge>
                </div>

                <div className={styles['action-cell']}>
                  {canOpenDelivery ? (
                    <Button
                      variant="primary"
                      size="xs"
                      onClick={() => {
                        void openDeliveryModal(row);
                      }}
                    >
                      Entregar
                    </Button>
                  ) : null}
                  {showEditWorkoutAction ? (
                    <KebabMenu
                      items={[
                        {
                          icon: 'edit',
                          label: 'Editar treino',
                          onClick: () => openEditWorkoutModal(row),
                        },
                      ]}
                    />
                  ) : null}
                  {!canOpenDelivery && !showEditWorkoutAction ? (
                    <span className={styles['empty-value']}>—</span>
                  ) : null}
                </div>
              </DataTable.Row>
            );
          })}
        </DataTable>
      )}

      {rows.length > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <ManagedFupResponseModal
        fup={pendingFupView}
        canManage={canManageFup}
        onClose={() => setPendingFupView(null)}
        onSaved={() => {
          refetch();
        }}
        onDeleted={() => {
          refetch();
        }}
      />

      <NewStudentAnswersModal
        open={Boolean(pendingNewStudentAnswers)}
        studentId={pendingNewStudentAnswers?.studentId ?? null}
        studentName={pendingNewStudentAnswers?.studentName}
        onClose={() => setPendingNewStudentAnswers(null)}
      />

      <StudentNotesModal
        open={Boolean(pendingNotesView)}
        studentId={pendingNotesView?.studentId}
        studentName={pendingNotesView?.studentName ?? ''}
        notes={pendingNotesView?.history ?? []}
        summary={pendingNotesView?.summary}
        value={pendingNotesView?.newNote ?? ''}
        loading={submittingNotes || loadingNotesHistory}
        canWriteObservations={canWriteObservations}
        onChange={(nextValue) => {
          setPendingNotesView((current) => current ? { ...current, newNote: nextValue } : current);
        }}
        onSummaryGenerated={(summary) => {
          setPendingNotesView((current) => current ? { ...current, summary } : current);
        }}
        onCancel={() => setPendingNotesView(null)}
        onSubmit={handleNotesSubmit}
        onDeleteNote={handleDeleteNote}
      />

      <ConfirmModal
        open={Boolean(pendingCoachEdit)}
        title={pendingCoachEdit ? `Professora — ${pendingCoachEdit.studentName}` : 'Professora'}
        message={pendingCoachEdit ? 'Selecione a professora responsável por esta aluna.' : ''}
        confirmLabel="Salvar"
        cancelLabel="Cancelar"
        variant="info"
        loading={submittingCoachEdit}
        onCancel={() => setPendingCoachEdit(null)}
        onConfirm={handleCoachEditConfirm}
      >
        <div className={styles['coach-modal-fields']}>
          <SearchableSelect
            value={pendingCoachEdit?.coachId ?? ''}
            onChange={(value) => {
              setPendingCoachEdit((current) => current ? { ...current, coachId: value } : current);
            }}
            options={coachSelectOptions}
            placeholder={loadingCoachOptions ? 'Carregando professoras...' : 'Selecione a professora'}
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhuma professora encontrada"
            disabled={submittingCoachEdit || loadingCoachOptions}
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingDelivery)}
        title="Entregar treino"
        message={pendingDelivery ? `Registre os dados de entrega para ${pendingDelivery.studentName}.` : ''}
        confirmLabel="Finalizar"
        cancelLabel="Cancelar"
        variant="warning"
        width={DELIVERY_WORKOUT_MODAL_WIDTH_PX}
        alignment="left"
        loading={finalizingDelivery}
        onCancel={() => {
          if (isCatalogSearchOpen) {
            setIsCatalogSearchOpen(false);
            return;
          }
          setPendingDelivery(null);
        }}
        onConfirm={handleFinalizeDelivery}
      >
        <div className={styles['context-fields']}>
          <div className={styles['next-training-row']}>
            <FormInput
              id="performance-current-training-code"
              label="Treino anterior"
              value={pendingDelivery?.previousTrainingCode ?? ''}
              onChange={(event) => {
                const previousTrainingCode = event.target.value.toUpperCase();
                setPendingDelivery((current) => (
                  current ? { ...current, previousTrainingCode } : current
                ));
              }}
              placeholder="Ex.: B07"
              disabled={finalizingDelivery}
              containerClassName={styles['next-training-input']}
              className={styles['training-code-input']}
            />
            <Button
              type="button"
              variant="outline"
              size="small"
              className={styles['search-training-button']}
              disabled={finalizingDelivery}
              onClick={() => {
                setCatalogSearchTarget('previous');
                setIsCatalogSearchOpen(true);
              }}
            >
              Buscar
            </Button>
          </div>
          <div className={styles['next-training-row']}>
            <FormInput
              id="performance-next-training-code"
              label={getDeliveredTrainingLabel()}
              value={
                pendingDelivery?.sourceTrainingId != null
                  ? (pendingDelivery.selectedTrainingName ?? '')
                  : (pendingDelivery?.nextTrainingCode ?? '')
              }
              onChange={(event) => {
                const nextTrainingCode = event.target.value.toUpperCase();
                setPendingDelivery((current) => (
                  current
                    ? {
                      ...current,
                      nextTrainingCode,
                      sourceTrainingId: null,
                      selectedTrainingName: null,
                      catalogTrainingLabel: null,
                    }
                    : current
                ));
              }}
              placeholder="Ex.: B07"
              disabled={finalizingDelivery}
              required
              containerClassName={styles['next-training-input']}
              className={pendingDelivery?.sourceTrainingId != null ? undefined : styles['training-code-input']}
            />
            <Button
              type="button"
              variant="outline"
              size="small"
              className={styles['search-training-button']}
              disabled={finalizingDelivery}
              onClick={() => {
                setCatalogSearchTarget('next');
                setIsCatalogSearchOpen(true);
              }}
            >
              Buscar
            </Button>
          </div>
          <FormInput
            id="performance-delivered-date"
            label="Data do treino"
            type="date"
            value={pendingDelivery?.deliveredDate ?? ''}
            onChange={(event) => {
              const deliveredDate = event.target.value;
              setDeliveryDateError(undefined);
              setPendingDelivery((current) => (
                current ? { ...current, deliveredDate } : current
              ));
            }}
            error={deliveryDateError}
            required
            disabled={finalizingDelivery}
          />
          <FormTextarea
            id="performance-note"
            label="Nova observação"
            value={pendingDelivery?.note ?? ''}
            onChange={(event) => {
              const note = event.target.value;
              setPendingDelivery((current) => current ? { ...current, note } : current);
            }}
            rows={4}
            placeholder="Ex.: Relatou dor lombar, reduzir volume na próxima semana"
            disabled={finalizingDelivery}
            error={
              pendingDelivery?.note.trim()
                ? validateStudentNote(pendingDelivery.note, { required: false }) || undefined
                : undefined
            }
          />
          <StudentNotesHistorySection
            notes={pendingDelivery?.history ?? []}
            onDeleteNote={handleDeleteNote}
          />
        </div>
      </ConfirmModal>

      <TrainingCatalogSearchModal
        open={isCatalogSearchOpen}
        onCancel={() => setIsCatalogSearchOpen(false)}
        onSelect={(training: TrainingItem) => {
          const sourceTrainingId = Number.parseInt(training.id, 10);
          const catalogTrainingLabel = training.name.trim().toUpperCase();
          if (!Number.isInteger(sourceTrainingId) || sourceTrainingId <= 0) {
            alert('Não foi possível identificar o treino selecionado.');
            return;
          }
          if (!catalogTrainingLabel) {
            alert('Este treino não possui código.');
            return;
          }

          setPendingDelivery((current) => {
            if (!current) {
              return current;
            }

            if (catalogSearchTarget === 'previous') {
              return {
                ...current,
                previousTrainingCode: catalogTrainingLabel,
              };
            }

            return {
              ...current,
              sourceTrainingId,
              selectedTrainingName: training.name,
              catalogTrainingLabel,
            };
          });
          setIsCatalogSearchOpen(false);
        }}
      />

      <LaunchWorkoutModal
        open={launchWorkoutOpen}
        initialStudentId={editingWorkoutRow?.student_id}
        initialStudentName={editingWorkoutRow?.student_name}
        historyEdit={editingWorkoutRow?.workout_history_id ? {
          historyId: editingWorkoutRow.workout_history_id,
          assignmentId: editingWorkoutRow.id,
          competence: toDateOnly(editingWorkoutRow.last_workout_date).slice(0, 7)
            || toDateOnly(editingWorkoutRow.delivered_at).slice(0, 7)
            || toDateOnly(editingWorkoutRow.expected_delivery_date).slice(0, 7),
          previousTrainingCode: editingWorkoutRow.current_training_code ?? '',
          expectedDeliveryDate: toDateOnly(editingWorkoutRow.expected_delivery_date)
            || toDateOnly(editingWorkoutRow.delivered_at),
          status: editingWorkoutRow.status === 'delivered' ? 'delivered' : 'scheduled',
          lastWorkoutDate: toDateOnly(editingWorkoutRow.last_workout_date),
          workoutCode: editingWorkoutRow.history_workout_code
            ?? editingWorkoutRow.next_training_code
            ?? '',
          deliveredAt: editingWorkoutRow.delivered_at ?? '',
          deliveredByPortalUserId: editingWorkoutRow.delivered_by_portal_user_id ?? 0,
        } : undefined}
        cycleEdit={editingWorkoutRow && !editingWorkoutRow.workout_history_id ? {
          assignmentId: editingWorkoutRow.id,
          previousTrainingCode: editingWorkoutRow.current_training_code ?? '',
          nextTrainingCode: editingWorkoutRow.next_training_code ?? '',
          expectedDeliveryDate: toDateOnly(editingWorkoutRow.expected_delivery_date),
          status: editingWorkoutRow.status === 'delivered' ? 'delivered' : 'scheduled',
          deliveredAt: editingWorkoutRow.delivered_at ?? undefined,
        } : undefined}
        onClose={closeLaunchWorkoutModal}
        onSaved={() => {
          setEditingWorkoutRow(null);
          refetch();
        }}
      />
    </PageContainer>
  );
};

export default Performance;
